import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { buildScopeTranslate } from "lib/I18n";

const tInfo = buildScopeTranslate("views.content.agentforce.assessments.info_bubble");
const tCommon = buildScopeTranslate("views.content.agentforce.assessments.common");

const InfoAssessmentModal = ({ open, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const modal = modalRef.current;
    if (modal) {
      modal.addEventListener('requestclose', onClose);
      return () => {
        modal.removeEventListener('requestclose', onClose);
      };
    }
  }, [onClose]);

  return (
    <tds-modal
      ref={modalRef}
      open={open}
      size="small"
      dismissOnClickOutside={true}
      class="th-agentforce-assessment__modal">

      {/* Header */}
      <div slot="heading" className="slds-text-heading_medium" data-testid="modal-heading">
        {tInfo("title")}
      </div>

      {/* Content */}
      <div slot="content">
        {/* Content body */}
        <div>
          <p className="slds-text-body_regular slds-m-bottom_medium">
            {tInfo("subtitle")}
          </p>

          <p className="slds-text-body_regular slds-m-bottom_medium">
            <strong>{tInfo("how_it_works_label")}</strong>
          </p>

          {/* List items with checkmark icons - single grid container */}
          <div className="slds-grid slds-wrap slds-grid_vertical-align-start slds-m-bottom_medium th-agentforce-assessment__modal-list">
            {/* Row 1: The Setup */}
            <div className="slds-col slds-shrink slds-p-right_x-small slds-m-top_x-small">
              <tds-icon
                sprite="action"
                symbol="check"
                size="x-small"
                assistiveText={tCommon("complete_icon")}
              />
            </div>
            <div className="slds-col th-agentforce-assessment__modal-list-content">
              <p className="slds-text-body_regular">
                <strong>{tInfo("setup_title")}</strong> {tInfo("setup_body")}
              </p>
            </div>

            {/* Row 2: Two Ways to Play */}
            <div className="slds-col slds-shrink slds-p-right_x-small slds-m-top_x-small">
              <tds-icon
                sprite="action"
                symbol="check"
                size="x-small"
                assistiveText={tCommon("complete_icon")}
              />
            </div>
            <div className="slds-col th-agentforce-assessment__modal-list-content">
              <p className="slds-text-body_regular">
                <strong>{tInfo("two_ways_title")}</strong> {tInfo("two_ways_body")}
              </p>
            </div>

            {/* Row 3: Scoring Rubric */}
            <div className="slds-col slds-shrink slds-p-right_x-small slds-m-top_x-small">
              <tds-icon
                sprite="action"
                symbol="check"
                size="x-small"
                assistiveText={tCommon("complete_icon")}
              />
            </div>
            <div className="slds-col th-agentforce-assessment__modal-list-content">
              <div className="slds-text-body_regular">
                <p className="slds-m-bottom_x-small">
                  <strong>{tInfo("scoring_rubric_title")}</strong> {tInfo("scoring_rubric_body")}
                </p>
                <ul className="slds-list_dotted slds-m-left_large">
                  <li>{tInfo("scoring_points.needs_improvement")}</li>
                  <li>{tInfo("scoring_points.mostly_meets")}</li>
                  <li>{tInfo("scoring_points.meets")}</li>
                  <li>{tInfo("scoring_points.exceeds")}</li>
                </ul>
              </div>
            </div>

            {/* Row 4: Your Total Score */}
            <div className="slds-col slds-shrink slds-p-right_x-small slds-m-top_x-small">
              <tds-icon
                sprite="action"
                symbol="check"
                size="x-small"
                assistiveText={tCommon("complete_icon")}
              />
            </div>
            <div className="slds-col th-agentforce-assessment__modal-list-content">
              <p className="slds-text-body_regular">
                <strong>{tInfo("total_score_title")}</strong> {tInfo("total_score_body")}
              </p>
            </div>

            {/* Row 5: Your Data */}
            <div className="slds-col slds-shrink slds-p-right_x-small slds-m-top_x-small">
              <tds-icon
                sprite="action"
                symbol="check"
                size="x-small"
                assistiveText={tCommon("complete_icon")}
              />
            </div>
            <div className="slds-col th-agentforce-assessment__modal-list-content">
              {/* eslint-disable-next-line react/no-danger */}
              <p
                className="slds-text-body_regular"
                dangerouslySetInnerHTML={{
                  __html: `<strong>${tInfo("your_data_title")}</strong> ${tInfo("your_data_body")}`
                }}
              />
            </div>

            {/* Row 6: Feedback */}
            <div className="slds-col slds-shrink slds-p-right_x-small slds-m-top_x-small">
              <tds-icon
                sprite="action"
                symbol="check"
                size="x-small"
                assistiveText={tCommon("complete_icon")}
              />
            </div>
            <div className="slds-col th-agentforce-assessment__modal-list-content">
              {/* eslint-disable-next-line react/no-danger */}
              <p
                className="slds-text-body_regular"
                dangerouslySetInnerHTML={{
                  __html: `<strong>${tInfo("feedback_welcome_title")}</strong> ${tInfo("feedback_welcome_body")}`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer with button - white background, no border */}
      <div slot="footer" className="th-agentforce-assessment__modal-footer">
        <tds-button variant="brand" onClick={onClose} data-testid="modal-close-btn">
          <span>{tInfo("close_button")}</span>
        </tds-button>
      </div>
    </tds-modal>
  );
};

InfoAssessmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default InfoAssessmentModal;
